import {
  ClaimAssigneeRelationshipCanon,
  GetAssignmentsForAssignableResponse,
} from '@eigtech/assignments-types'
import { GetClaimDetailsResponse } from '@eigtech/claims-v2-types'
import { Contact } from '@eigtech/contacts-types'
import { ArrayElement } from '@eigtech/function-utils'
import { ensureClaimCorn } from '@eigtech/shared-corn-helper'
import { GetClaimSummariesResponse } from '@eigtech/summarizer-types'
import { useMutation, useQueryClient } from '@eigtech/ui-shared-api'
import {
  assign,
  assignmentsQueryKeys,
  useInvalidateAssignmentsForAssignable,
} from '@eigtech/ui-shared-assignments'
import { getContactName } from '@eigtech/ui-shared-contacts'
import { getLog } from '@eigtech/ui-shared-logging'
import { useCallback } from 'react'
import { claimsQueryKeys } from '../claims/constants'
import { useInvalidateClaim, useInvalidateClaimsSummaries } from '../claims/invalidateClaims'
import { makeClaimAssignment } from './assignEntityToClaim'

const log = getLog()

export function useOptimisticallyUpdateAssignedClaimContact() {
  const queryClient = useQueryClient()
  const invalidateClaimsSummaries = useInvalidateClaimsSummaries()
  const invalidateClaim = useInvalidateClaim()
  const invalidateAssignmentsForClaim = useInvalidateAssignmentsForAssignable()

  const onMutate = useCallback(
    async ({
      claimNumber,
      contact,
      type,
    }: {
      claimNumber: string
      contact: Contact | undefined
      type: ClaimAssigneeRelationshipCanon
    }) => {
      const claimCorn = ensureClaimCorn(claimNumber)

      const basicContact = contact
        ? {
            id: contact.contactId,
            name: getContactName(contact),
          }
        : undefined

      // update claims list
      const listQueryKey = claimsQueryKeys.list()
      await queryClient.cancelQueries({ queryKey: listQueryKey })

      const previousClaims = queryClient.getQueriesData<GetClaimSummariesResponse>({
        queryKey: listQueryKey,
      })

      previousClaims.forEach(([key, data]) => {
        if (!data) return

        queryClient.setQueryData<GetClaimSummariesResponse>(key, {
          ...data,
          claims: (data?.claims ?? []).map((claim) =>
            claimNumber === claim.claimNumber
              ? {
                  ...claim,
                  [type]: basicContact,
                }
              : claim
          ),
        })
      })

      // update claim detail
      const detailQueryKey = claimsQueryKeys.detail(claimNumber)
      await queryClient.cancelQueries({ queryKey: detailQueryKey })

      const previousClaimDetail = queryClient.getQueryData<GetClaimDetailsResponse>(detailQueryKey)

      if (previousClaimDetail) {
        queryClient.setQueryData<GetClaimDetailsResponse>(detailQueryKey, {
          ...previousClaimDetail,
          [type]: basicContact,
        })
      }

      const assignmentQueryKey = assignmentsQueryKeys.assignable.assignments(claimCorn)
      await queryClient.cancelQueries({ queryKey: assignmentQueryKey })
      const previousAssignment =
        queryClient.getQueryData<GetAssignmentsForAssignableResponse>(assignmentQueryKey)

      if (previousAssignment) {
        const updatedAssignment = [
          ...previousAssignment.filter((assignment) => {
            const canHaveMultipleAssignments = isMultiAssignee(type)
            const isClaim = assignment.assignable.type === 'claim'
            const hasSameRelationship = assignment.assignable.assigneeRelationship === type

            return isClaim && (!hasSameRelationship || canHaveMultipleAssignments)
          }),
        ]

        if (contact) {
          const claimAssignment = {
            ...makeClaimAssignment({
              assigneeRelationship: type,
              claimNumber,
              entity: contact,
              type: 'contact',
            }),
            assignmentDate: new Date().toISOString(),
            requestActor: 'user',
          } as ArrayElement<GetAssignmentsForAssignableResponse>
          updatedAssignment.push(claimAssignment)
        }

        queryClient.setQueryData<GetAssignmentsForAssignableResponse>(
          assignmentQueryKey,
          updatedAssignment
        )
      }

      return {
        previousClaims,
        previousClaimDetail,
      }
    },
    [queryClient]
  )

  const onError = useCallback(
    (
      error: unknown,
      { claimNumber }: { claimNumber: string },
      context: Awaited<ReturnType<typeof onMutate>> | undefined
    ) => {
      log.error('could not set assignee', { error })

      const claimCorn = ensureClaimCorn(claimNumber)

      context?.previousClaims.forEach(([key, data]) => {
        queryClient.setQueryData(key, data)
      })

      if (context?.previousClaimDetail?.claimNumber) {
        queryClient.setQueryData(
          claimsQueryKeys.detail(context.previousClaimDetail.claimNumber),
          context.previousClaimDetail
        )
      }

      invalidateClaimsSummaries()
      invalidateClaim(claimNumber)
      invalidateAssignmentsForClaim(claimCorn)
    },
    [invalidateAssignmentsForClaim, invalidateClaim, invalidateClaimsSummaries, queryClient]
  )

  const onSuccess = useCallback(
    (claimNumber: string) => {
      const claimCorn = ensureClaimCorn(claimNumber)

      // invalidate after a few seconds on success
      // (it can take a lil bit for events on backend to catch up)
      setTimeout(() => {
        invalidateAssignmentsForClaim(claimCorn)
        invalidateClaimsSummaries()
        invalidateClaim(claimNumber)
      }, 30000)
    },
    [invalidateAssignmentsForClaim, invalidateClaim, invalidateClaimsSummaries]
  )

  return { onMutate, onError, onSuccess }
}

export type AssignContactToClaimProps = {
  assigneeRelationship: Exclude<ClaimAssigneeRelationshipCanon, 'job'>
  claimNumber: string
  contact: Contact
}

export function useAssignContactToClaim() {
  const { onError, onMutate, onSuccess } = useOptimisticallyUpdateAssignedClaimContact()

  return useMutation({
    mutationFn: (props: AssignContactToClaimProps) => {
      return assign(makeClaimAssignment({ ...props, type: 'contact', entity: props.contact }))
    },
    onMutate({ assigneeRelationship, claimNumber, contact }) {
      return onMutate({
        claimNumber,
        type: assigneeRelationship,
        contact,
      })
    },
    onSuccess(__, { claimNumber }) {
      onSuccess(claimNumber)
    },
    onError: (error, variables, context) => onError(error, variables, context),
  })
}

function isMultiAssignee(assigneeRelationship: ClaimAssigneeRelationshipCanon) {
  const multiAssigneeList: ClaimAssigneeRelationshipCanon[] = ['legalRep']
  return multiAssigneeList.includes(assigneeRelationship)
}
